import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { AppContext } from "../../context/AppContext";

/**
 * public partner list item
 */
type PublicPartnerListItem = {
  hashId: string;
  name: string;
  name2?: string | null;
  description?: string | null;
  street?: string | null;
  housenumber?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
  email?: string | null;
  fon?: string | null;
  www?: string | null;
  linkedin?: string | null;
};

/**
 * public partner list item with a stable local id
 */
type PublicPartnerListEntry = PublicPartnerListItem & {
  id: string;
};

/**
 * sanitized link
 */
type SanitizedLink = {
  href: string;
  label: string;
};

/**
 * which edges of the horizontally scrollable register still have content behind them
 */
type RegisterOverflow = {
  start: boolean;
  end: boolean;
};

/**
 * per page size used to load the complete public partner list
 */
const PER_PAGE = 500;

/**
 * hard safety cap so a broken pagination meta can never produce an endless loop
 */
const MAX_PAGES = 50;

/**
 * throttle interval for the reading position tracker
 */
const THROTTLE_MS = 100;

/**
 * fallback group for names not starting with A-Z
 */
const GROUP_OTHER = "#";

/**
 * query parameter carrying the search term
 */
const SEARCH_PARAM = "q";

/**
 * where a prospective participant is sent. same destination the login page already
 * links to, since no dedicated participation page exists yet
 */
const PARTNER_CONTACT_URL = "https://www.dia-software.de";

/**
 * A-Z register letters
 */
const LETTERS: string[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/**
 * letters that NFD does not decompose, mapped to their A-Z register group
 */
const GROUP_LETTER_TRANSLITERATIONS: { [letter: string]: string } = {
  "Ø": "O",
  "Æ": "A",
  "Ł": "L",
  "Þ": "T",
  "Ð": "D"
};

/**
 * getPartnerId
 * stable id, falls back to the address signature when the api delivers no hashId
 * @param partner
 */
function getPartnerId(partner: PublicPartnerListItem): string {
  return partner?.hashId
    || `${partner?.name || ""}-${partner?.street || ""}-${partner?.zip || ""}-${partner?.city || ""}`;
}

/**
 * german umlauts and eszett in their ascii spelling, applied before NFD strips
 * the diacritics away
 */
const SEARCH_TRANSLITERATIONS: [RegExp, string][] = [
  [/ä/g, "ae"],
  [/ö/g, "oe"],
  [/ü/g, "ue"],
  [/ß/g, "ss"]
];

/**
 * normalizeForSearch
 * lowercase and diacritics insensitive ("München" -> "munchen")
 * @param value
 */
function normalizeForSearch(value?: string | null): string {
  return (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * normalizeForSearchAscii
 * the ascii spelling a german keyboard produces ("München" -> "muenchen")
 * @param value
 */
function normalizeForSearchAscii(value?: string | null): string {
  const transliterated = SEARCH_TRANSLITERATIONS.reduce(
    (result, [pattern, replacement]) => result.replace(pattern, replacement),
    (value || "").toLowerCase()
  );

  return normalizeForSearch(transliterated);
}

/**
 * matchesSearch
 * a haystack matches when either spelling variant contains the query
 * @param haystack
 * @param query
 */
function matchesSearch(haystack: string, query: string): boolean {
  return normalizeForSearch(haystack).includes(query)
    || normalizeForSearchAscii(haystack).includes(query);
}

/**
 * getGroupLetter
 * locale normalized first letter of a name ("Ösi" -> "O", "Ørsted" -> "O", "3M" -> "#")
 * @param name
 */
function getGroupLetter(name?: string | null): string {
  const normalized = (name || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .charAt(0)
    .toUpperCase();

  const first = GROUP_LETTER_TRANSLITERATIONS[normalized] || normalized;

  return first >= "A" && first <= "Z" ? first : GROUP_OTHER;
}

/**
 * international dialling prefixes for the countries this list actually contains.
 * only used to reject a number that contradicts its own address, so a country
 * missing from the table never suppresses anything
 */
const COUNTRY_DIALLING_CODES: { [country: string]: string } = {
  AT: "43",
  BE: "32",
  CH: "41",
  CZ: "420",
  DE: "49",
  DK: "45",
  ES: "34",
  FR: "33",
  GB: "44",
  IT: "39",
  LU: "352",
  NL: "31",
  PL: "48",
  SE: "46"
};

/**
 * sanitizePhoneNumber
 * dialable number, digits only with an optional single leading plus
 * @param value
 */
function sanitizePhoneNumber(value?: string | null): string {
  return (value || "")
    .replace(/[^\d+]/g, "")
    .replace(/(?!^)\+/g, "");
}

/**
 * isDialable
 * a number whose country code contradicts its own postal address is a data error
 * often enough that one tap must not place the call. the number stays visible
 * verbatim, it just stops being a link. numbers without a country code, and
 * countries missing from the table, are always dialable
 * @param value
 * @param country
 */
function isDialable(value: string, country?: string | null): boolean {
  if (!value.startsWith("+")) {
    return true;
  }

  const expected = COUNTRY_DIALLING_CODES[(country || "").toUpperCase()];

  return !expected || value.startsWith(`+${expected}`);
}

/**
 * sanitizeWebsite
 * the api may deliver markdown links like "[www.example.com](https://www.example.com)"
 * @param value
 */
function sanitizeWebsite(value?: string | null): SanitizedLink | null {
  const trimmed = (value || "").trim();

  if (!trimmed) {
    return null;
  }

  const markdown = trimmed.match(/^\[([^\]]*)]\(([^)]+)\)$/);
  let href = (markdown ? markdown[2] : trimmed).trim();

  if (!href) {
    return null;
  }

  if (!/^https?:\/\//i.test(href)) {
    href = "https://" + href.replace(/^\/+/, "");
  }

  let label = href.replace(/^https?:\/\//i, "").replace(/\/+$/, "");

  try {
    label = new URL(href).host || label;
  } catch (error) {
    // keep the plain label
  }

  return { href, label };
}

/**
 * formatPhrase
 * fills the %s placeholders of a translated phrase in order. UtilI18N.t() accepts
 * arguments but crashes on them in this build ("dist.apply is not a function" out
 * of its sprintf import), so the substitution happens here instead
 * @param template
 * @param values
 */
function formatPhrase(template: string, values: (string | number)[]): string {
  let index = 0;

  return template.replace(/%s/g, () => {
    const value = values[index];
    index += 1;

    return value === undefined ? "" : String(value);
  });
}

/**
 * highlightMatch
 * splits a value around every occurrence of the query so the matched runs can be
 * marked. offsets are only reliable while normalizing preserves length, so the
 * ascii spelling ("strasse" for "Straße") still matches but stays unmarked rather
 * than marking the wrong characters
 * @param value
 * @param query
 */
function highlightMatch(value: string, query: string): (string | { match: string })[] {
  if (!query) {
    return [value];
  }

  // both spellings are candidates, the one that actually hits decides
  const normalized = [normalizeForSearch(value), normalizeForSearchAscii(value)]
    .find((candidate) => candidate.length === value.length && candidate.includes(query));

  if (!normalized) {
    return [value];
  }

  const parts: (string | { match: string })[] = [];
  let cursor = 0;
  let index = normalized.indexOf(query);

  while (index !== -1) {
    if (index > cursor) {
      parts.push(value.slice(cursor, index));
    }

    parts.push({ match: value.slice(index, index + query.length) });
    cursor = index + query.length;
    index = normalized.indexOf(query, cursor);
  }

  if (cursor < value.length) {
    parts.push(value.slice(cursor));
  }

  return parts;
}

/**
 * NetworkParticipantsPage
 * public listing of all active interlink partners
 * @constructor
 */
export function NetworkParticipantsPage() {
  const {
    I18N,
    services: {
      servicePartner
    }
  } = useContext(AppContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const [partners, setPartners] = useState<PublicPartnerListEntry[]>([]);
  /**
   * search term, held in the url so a filtered view can be shared and survives a reload
   */
  const search: string = searchParams.get(SEARCH_PARAM) || "";
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(false);
  const [brokenLogos, setBrokenLogos] = useState<{ [id: string]: boolean }>({});
  const [activeLetter, setActiveLetter] = useState<string>("");
  const [registerOverflow, setRegisterOverflow] = useState<RegisterOverflow>({ start: false, end: false });
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const sectionRefs = useRef<{ [letter: string]: HTMLElement | null }>({});
  const registerRef = useRef<HTMLDivElement | null>(null);
  const letterRefs = useRef<{ [letter: string]: HTMLButtonElement | null }>({});

  /**
   * locale
   */
  const locale: string = I18N.getLocale?.() || "de";

  /**
   * handleSearch
   * an empty term drops the parameter instead of leaving "?q=" behind, and every
   * keystroke replaces the entry so typing does not fill the back button's history
   * @param value
   */
  const handleSearch = useCallback((value: string): void => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);

      if (value) {
        next.set(SEARCH_PARAM, value);
      } else {
        next.delete(SEARCH_PARAM);
      }

      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /**
   * load the complete public partner list page by page
   */
  const loadPartners = useCallback(async (): Promise<void> => {
    if (hasFetchedRef.current || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    try {
      setIsLoading(true);
      setHasError(false);
      setIsOffline(false);

      let page = 1;
      let lastPage = 1;
      const collected: PublicPartnerListItem[] = [];

      do {
        const response = await servicePartner.getPublicActiveList({
          page,
          perPage: PER_PAGE
        });

        const pageData: PublicPartnerListItem[] = Array.isArray(response?.data) ? response.data : [];
        collected.push(...pageData);

        if (!pageData.length) {
          break;
        }

        const responseLastPage = Number(response?.meta?.last_page);
        if (Number.isFinite(responseLastPage) && responseLastPage > 0) {
          lastPage = responseLastPage;
        } else if (pageData.length < PER_PAGE) {
          lastPage = page;
        } else {
          lastPage = page + 1;
        }

        page += 1;

        // guard against a missing or broken pagination meta
        if (page > MAX_PAGES) {
          break;
        }
      } while (page <= lastPage);

      const uniqueById = new Map<string, PublicPartnerListEntry>();
      collected.forEach((partner) => {
        const id = getPartnerId(partner);

        if (!uniqueById.has(id)) {
          uniqueById.set(id, { ...partner, id });
        }
      });

      setPartners(Array.from(uniqueById.values()));
      setIsLoaded(true);
      hasFetchedRef.current = true;
    } catch (error) {
      console.error("Could not load publicActiveList", error);
      // a dropped connection and a failing backend need different advice, and
      // navigator.onLine is the only signal available before the request resolves
      setIsOffline(typeof navigator !== "undefined" && navigator.onLine === false);
      setHasError(true);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [servicePartner]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  /**
   * retry after a failed load
   */
  const handleRetry = useCallback((): void => {
    if (isFetchingRef.current) {
      return;
    }

    hasFetchedRef.current = false;
    setHasError(false);
    loadPartners();
  }, [loadPartners]);

  /**
   * alphabetically sorted partners
   */
  const sortedPartners = useMemo(
    () => [...partners].sort(
      (a, b) => (a.name || "").localeCompare(b.name || "", locale, { sensitivity: "base", numeric: true })
    ),
    [partners, locale]
  );

  /**
   * filtered partners
   */
  const normalizedSearch = normalizeForSearch(search.trim());
  const filteredPartners = useMemo(
    () => (!normalizedSearch ? sortedPartners : sortedPartners.filter((partner) => matchesSearch([
      partner.name,
      partner.name2,
      partner.street,
      partner.zip,
      partner.city,
      partner.country
    ]
      .filter(Boolean)
      .join(" "), normalizedSearch))),
    [normalizedSearch, sortedPartners]
  );

  /**
   * partners grouped by first letter
   */
  const groups = useMemo(() => {
    const grouped: { [letter: string]: PublicPartnerListEntry[] } = {};

    filteredPartners.forEach((partner) => {
      const letter = getGroupLetter(partner.name);

      if (!grouped[letter]) {
        grouped[letter] = [];
      }

      grouped[letter].push(partner);
    });

    return grouped;
  }, [filteredPartners]);

  /**
   * register letters, "#" first when needed
   * derived from the unfiltered list so the row geometry stays stable while searching
   */
  const registerLetters = useMemo(
    () => (sortedPartners.some((partner) => getGroupLetter(partner.name) === GROUP_OTHER)
      ? [GROUP_OTHER, ...LETTERS]
      : LETTERS),
    [sortedPartners]
  );

  /**
   * available group letters in document order
   */
  const groupLetters = useMemo(
    () => registerLetters.filter((letter) => !!groups[letter]),
    [registerLetters, groups]
  );

  /**
   * getSectionId
   * @param letter
   */
  const getSectionId = (letter: string): string => `letter-${letter === GROUP_OTHER ? "other" : letter}`;

  /**
   * getScrollParent
   * the app scrolls inside dia-grid, not on the document
   * @param element
   */
  const getScrollParent = (element: HTMLElement): HTMLElement | null => {
    let current: HTMLElement | null = element.parentElement;

    while (current) {
      const overflowY = window.getComputedStyle(current).overflowY;

      if ((overflowY === "auto" || overflowY === "scroll")
        && current.scrollHeight > current.clientHeight) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  };

  /**
   * syncRegisterOverflow
   * the register hides its scrollbar on purpose, so the only remaining cue that
   * letters continue past the edge is the fade this state drives
   */
  const syncRegisterOverflow = useCallback((): void => {
    const register = registerRef.current;

    if (!register) {
      return;
    }

    const maxScrollLeft = register.scrollWidth - register.clientWidth;

    setRegisterOverflow((current) => {
      // 1px tolerance: fractional layout widths otherwise report a permanent overflow
      const next = {
        start: register.scrollLeft > 1,
        end: register.scrollLeft < maxScrollLeft - 1
      };

      return current.start === next.start && current.end === next.end ? current : next;
    });
  }, []);

  /**
   * syncActiveLetter
   * the group whose heading last passed below the register is the one the
   * visitor is reading, and the register marks it
   */
  const syncActiveLetter = useCallback((): void => {
    const register = registerRef.current;

    if (!register) {
      return;
    }

    const threshold = register.getBoundingClientRect().bottom + 8,
      lastLetter = groupLetters[groupLetters.length - 1],
      lastSection = lastLetter ? sectionRefs.current[lastLetter] : null;

    // the final group is usually shorter than the viewport, so its heading never
    // crosses the threshold. once it is fully visible the visitor is at the end
    if (lastSection && lastSection.getBoundingClientRect().bottom <= window.innerHeight) {
      setActiveLetter(lastLetter);
      return;
    }

    let current = "";

    groupLetters.forEach((letter) => {
      const section = sectionRefs.current[letter];

      if (section && section.getBoundingClientRect().top <= threshold) {
        current = letter;
      }
    });

    setActiveLetter(current || groupLetters[0] || "");
  }, [groupLetters]);

  /**
   * track the reading position and the register's own scroll offset
   */
  useEffect(() => {
    // capture phase on the document: the app scrolls inside dia-grid, not on the
    // window, and scroll events do not bubble. capturing catches every scroller
    // without having to resolve which ancestor is the scrolling one
    let lastRun = 0;
    let trailing = 0;

    const run = (): void => {
      lastRun = performance.now();
      syncActiveLetter();
      syncRegisterOverflow();
    };

    // a time based throttle, deliberately not requestAnimationFrame: rAF never
    // fires while the page is not compositing, and a dropped frame would leave
    // the marker frozen on whichever letter happened to be active
    const onScroll = (): void => {
      const elapsed = performance.now() - lastRun;

      if (elapsed >= THROTTLE_MS) {
        window.clearTimeout(trailing);
        run();
        return;
      }

      // trailing call so the marker still lands on the letter the visitor stopped at
      window.clearTimeout(trailing);
      trailing = window.setTimeout(run, THROTTLE_MS - elapsed);
    };

    run();

    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.clearTimeout(trailing);
      document.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
    };
  }, [syncActiveLetter, syncRegisterOverflow]);

  /**
   * keep the marked letter inside the register's own viewport. scrollLeft is set
   * directly instead of scrollIntoView, which would also scroll the page
   */
  useEffect(() => {
    const register = registerRef.current,
      button = activeLetter ? letterRefs.current[activeLetter] : null;

    if (!register || !button || register.scrollWidth <= register.clientWidth) {
      return;
    }

    const left = button.offsetLeft - (register.clientWidth - button.offsetWidth) / 2;

    register.scrollTo({
      left: Math.max(0, left),
      behavior: "smooth"
    });
  }, [activeLetter]);

  /**
   * scrollToLetter
   * smooth when the user allows motion, with an instant fallback for every
   * environment in which smooth scrolling silently does nothing
   * @param letter
   */
  const scrollToLetter = (letter: string): void => {
    const section = sectionRefs.current[letter];

    if (!section) {
      return;
    }

    const prefersReducedMotion = typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      section.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }

    const scrollParent = getScrollParent(section),
      scrollTopBefore = scrollParent ? scrollParent.scrollTop : window.scrollY;

    section.scrollIntoView({ behavior: "smooth", block: "start" });

    window.setTimeout(() => {
      const scrollTopAfter = scrollParent ? scrollParent.scrollTop : window.scrollY;

      if (Math.abs(scrollTopAfter - scrollTopBefore) < 2) {
        section.scrollIntoView({ behavior: "auto", block: "start" });
      }
    }, 400);
  };

  /**
   * skipToList
   * jumps past the register and hands the focus to the first group
   */
  const skipToList = (): void => {
    const letter = groupLetters[0];

    if (!letter) {
      return;
    }

    scrollToLetter(letter);
    sectionRefs.current[letter]?.focus({ preventScroll: true });
  };

  /**
   * getCountryName
   * @param code
   */
  const getCountryName = (code?: string | null): string => {
    if (!code) {
      return "";
    }

    try {
      const intl: any = Intl;

      if (typeof intl.DisplayNames !== "function") {
        return code;
      }

      return new intl.DisplayNames([locale], { type: "region" }).of(code.toUpperCase()) || code;
    } catch (error) {
      return code;
    }
  };

  /**
   * formatAddress
   * @param partner
   */
  const formatAddress = (partner: PublicPartnerListItem): string => {
    const street = [partner.street, partner.housenumber].filter(Boolean).join(" ").trim(),
      city = [partner.zip, partner.city].filter(Boolean).join(" ").trim(),
      country = getCountryName(partner.country);

    return [street, city, country].filter(Boolean).join(", ");
  };

  /**
   * canonical url, without the search parameter: every filtered view is the same page
   */
  const canonicalUrl: string = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname}`
    : "";

  /**
   * pageDescription
   */
  const pageDescription: string = I18N.t("Übersicht aller Netzwerkteilnehmer von DIA Interlink");

  /**
   * itemListSchema
   * the full list in document order, independent of the current search
   */
  const itemListSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: I18N.t("Netzwerkteilnehmer"),
    numberOfItems: sortedPartners.length,
    itemListElement: sortedPartners.map((partner, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Organization",
        name: partner.name,
        ...(partner.www ? { url: sanitizeWebsite(partner.www)?.href } : {}),
        ...(partner.city || partner.zip || partner.street
          ? {
            address: {
              "@type": "PostalAddress",
              ...(partner.street ? { streetAddress: [partner.street, partner.housenumber].filter(Boolean).join(" ") } : {}),
              ...(partner.zip ? { postalCode: partner.zip } : {}),
              ...(partner.city ? { addressLocality: partner.city } : {}),
              ...(partner.country ? { addressCountry: partner.country } : {})
            }
          }
          : {})
      }
    }))
    // I18N is stable for a given locale, and the locale change already forces a rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [sortedPartners, locale]);

  /**
   * render
   */
  return (
    <>
      <Helmet>
        <title>{I18N.t("Netzwerkteilnehmer")}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={I18N.t("Netzwerkteilnehmer")} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        {/* the page exists to be found: without an ItemList a crawler sees 28
            unrelated names, with it, one list of organizations. not wrapped in a
            condition: Helmet drops the whole block when a child is a boolean */}
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <div data-network-participants>
        <div data-network-participants-head>
          <h1>{I18N.t("Netzwerkteilnehmer")}</h1>

          {isLoaded && !!partners.length && (
            <p data-network-participants-total>
              {partners.length === 1
                ? I18N.t("Ein Unternehmen im Netzwerk")
                : formatPhrase(I18N.t("%s Unternehmen im Netzwerk"), [partners.length])}
            </p>
          )}

          <input
            data-network-participants-search
            type="search"
            value={search}
            aria-label={I18N.t("Nach Name oder Adresse suchen")}
            placeholder={I18N.t("Nach Name oder Adresse suchen")}
            onChange={(event) => handleSearch(event.target.value)}
          />
        </div>

        {/* after the search field, not before it: the point of the link is to skip
            the register, and from in front of the input it can never do that once
            the visitor has typed. hidden when there is no list to skip to */}
        {!!groupLetters.length && (
          <a
            data-network-participants-skip
            href={`#${getSectionId(groupLetters[0])}`}
            onClick={(event) => {
              event.preventDefault();
              skipToList();
            }}
          >
            {I18N.t("Zur Liste springen")}
          </a>
        )}

        {/* no <nav> element: the design system styles bare nav tags as mobile drawer menu */}
        {!!groupLetters.length && (
          <div
            role="navigation"
            data-network-participants-register
            aria-label={I18N.t("Alphabetisches Register")}
          >
            {/* the track scrolls and carries the fade. the mask has to stay off the
                sticky parent, or it would fade that element's own background too and
                let the cards underneath show through at the edges */}
            <div
              data-network-participants-register-track
              data-overflow-start={registerOverflow.start ? "" : undefined}
              data-overflow-end={registerOverflow.end ? "" : undefined}
              ref={registerRef}
            >
              {registerLetters.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  data-network-participants-letter
                  data-active={letter === activeLetter ? "" : undefined}
                  aria-current={letter === activeLetter ? "true" : undefined}
                  disabled={!groups[letter]}
                  onClick={() => scrollToLetter(letter)}
                  ref={(element) => {
                    letterRefs.current[letter] = element;
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>
        )}

        <div
          data-network-participants-message
          role="status"
          aria-live="polite"
        >
          {isLoading && I18N.t("Lade Netzwerkteilnehmer...")}

          {!isLoading && hasError && (
            <>
              {isOffline
                ? I18N.t("Keine Verbindung zum Internet")
                : I18N.t("Netzwerkteilnehmer konnten nicht geladen werden")}
              <button
                type="button"
                data-network-participants-action
                onClick={handleRetry}
              >
                {I18N.t("Erneut versuchen")}
              </button>
            </>
          )}

          {/* an empty list and a search without hits are different states: only the
              second one has something the visitor can undo */}
          {!isLoading && !hasError && isLoaded && !filteredPartners.length && !normalizedSearch
            && I18N.t("Keine Netzwerkteilnehmer gefunden")}

          {!isLoading && !hasError && isLoaded && !filteredPartners.length && !!normalizedSearch && (
            <>
              {formatPhrase(I18N.t("Keine Treffer für \"%s\""), [search.trim()])}
              <button
                type="button"
                data-network-participants-action
                onClick={() => handleSearch("")}
              >
                {I18N.t("Suche löschen")}
              </button>
            </>
          )}

          {!isLoading && !hasError && isLoaded && !!filteredPartners.length && !!normalizedSearch
            && (filteredPartners.length === 1
              ? formatPhrase(I18N.t("Ein Treffer von %s"), [partners.length])
              : formatPhrase(I18N.t("%s Treffer von %s"), [filteredPartners.length, partners.length]))}
        </div>

        {groupLetters.map((letter) => (
          <div
            key={letter}
            id={getSectionId(letter)}
            tabIndex={-1}
            data-network-participants-group
            // without this the programmatic jump announces a bare "group"
            aria-labelledby={`${getSectionId(letter)}-heading`}
            ref={(element) => {
              sectionRefs.current[letter] = element;
            }}
          >
            <h2
              id={`${getSectionId(letter)}-heading`}
              data-network-participants-group-heading
            >
              {letter}
            </h2>

            <div data-network-participants-group-list>
              {groups[letter].map((partner) => {
                const website = sanitizeWebsite(partner.www),
                  linkedin = sanitizeWebsite(partner.linkedin),
                  address = formatAddress(partner),
                  hasLogo = !!partner.hashId && !brokenLogos[partner.id];

                return (
                  <div
                    key={partner.id}
                    data-network-participants-card
                  >
                    <div data-network-participants-logo>
                      {hasLogo ? (
                        <img
                          src={servicePartner.getCompanyLogoUrl(partner.hashId)}
                          alt={partner.name}
                          loading="lazy"
                          onError={() => setBrokenLogos((current) => ({ ...current, [partner.id]: true }))}
                        />
                      ) : (
                        <span
                          data-network-participants-logo-fallback
                          aria-hidden="true"
                        >
                          {getGroupLetter(partner.name)}
                        </span>
                      )}
                    </div>

                    <div data-network-participants-info>
                      <h3 data-network-participants-name>
                        {highlightMatch(partner.name || "", normalizedSearch).map((part, index) => (
                          typeof part === "string"
                            ? part
                            : <mark key={index}>{part.match}</mark>
                        ))}
                      </h3>

                      {partner.name2 && (
                        <p data-network-participants-name2>
                          {highlightMatch(partner.name2, normalizedSearch).map((part, index) => (
                            typeof part === "string"
                              ? part
                              : <mark key={index}>{part.match}</mark>
                          ))}
                        </p>
                      )}

                      {partner.description && (
                        <p data-network-participants-description>{partner.description}</p>
                      )}

                      {address && (
                        <p data-network-participants-address>
                          {highlightMatch(address, normalizedSearch).map((part, index) => (
                            typeof part === "string"
                              ? part
                              : <mark key={index}>{part.match}</mark>
                          ))}
                        </p>
                      )}

                      {(website || linkedin || partner.email || partner.fon) && (
                        <p data-network-participants-links>
                          {website && (
                            <a
                              href={website.href}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {website.label}
                              <span data-visually-hidden>{I18N.t("(öffnet in neuem Tab)")}</span>
                            </a>
                          )}
                          {linkedin && (
                            <a
                              href={linkedin.href}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {I18N.t("LinkedIn")}
                              <span data-visually-hidden>{I18N.t("(öffnet in neuem Tab)")}</span>
                            </a>
                          )}
                          {partner.email && (
                            <a href={`mailto:${partner.email}`}>{partner.email}</a>
                          )}
                          {partner.fon && (
                            isDialable(sanitizePhoneNumber(partner.fon), partner.country)
                              ? <a href={`tel:${sanitizePhoneNumber(partner.fon)}`}>{partner.fon}</a>
                              : <span data-network-participants-fon-plain>{partner.fon}</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* shown when the list ends, and also on a search that found nothing: a visitor
            who just looked for a company that is not here is exactly the person this
            invitation is for, and it keeps the no-hit screen from being one grey line */}
        {isLoaded && !hasError && (!normalizedSearch || !filteredPartners.length) && (
          <div data-network-participants-outro>
            <p>{I18N.t("Ihr Unternehmen fehlt in dieser Liste?")}</p>
            <a
              data-network-participants-cta
              href={PARTNER_CONTACT_URL}
              target="_blank"
              rel="noreferrer"
            >
              {I18N.t("Teilnehmer werden")}
              <span data-visually-hidden>{I18N.t("(öffnet in neuem Tab)")}</span>
            </a>
          </div>
        )}
      </div>
    </>
  );
}
